// 趨勢引擎判定台：代碼 → 中文的對照，以及「這一列該怎麼呈現」的純函式。
//
// ⚠️ 這支**不 import 任何東西**（連 theme/tokens 都不 import），node 才能直接載入它跑測試：
//    server/scratch/_test_regime_console.mjs 會從 engine.mjs 靜態抽出引擎可能輸出的全部代碼，
//    逐一確認這裡都有中文；引擎新增代碼而這裡沒跟上，測試就會失敗。
//
// 原則：**對不到的代碼原樣顯示並標「未翻譯」，絕不變成空白**——空白會被讀成「沒有理由」。

/** 前向檢定主分析最早日期（台北）。後端 overview.freeze 有同一個值；這份只在 API 讀不到時當後援。 */
export const MAIN_ANALYSIS_NOT_BEFORE = "2028-10-08";

export const UNTRANSLATED_MARK = "〔未翻譯〕";

/** confirmedRegime／rawRegime 的全部可能值（engine.mjs：classifyRaw、applyTransition、initState） */
export const REGIME_LABEL = {
  BULL: "多頭",
  BEAR: "空頭",
  RANGE: "盤整",
  MIXED: "證據矛盾",
  BULL_TRANSITION: "多頭轉換中",
  BEAR_TRANSITION: "空頭轉換中",
  UNKNOWN: "未知",
};

/** dataStatus 的全部可能值（engine.mjs 的 dataStatus／barDataStatus；UNKNOWN 是 shadowRunner 的後援值） */
export const DATA_STATUS_LABEL = {
  VALID: "正常",
  TIME_GAP: "時間缺口",
  TIME_GAP_RECOVERY: "時間缺口恢復期",
  INVALID: "資料無效",
  PARAMS_CHANGED: "引擎參數中途變更",
  REVISION_CONFLICT: "資料修訂衝突",
  UNKNOWN: "未知",
};

// 固定代碼。包含 ineligibleReasons 與 reasonCode 兩個欄位會出現的值。
const REASON_EXACT = {
  STRUCTURE_CONFLICT: "高低點結構互相矛盾",
  CONFIRMED_THIS_BAR_AGE_0: "狀態這一根才剛確認，下一根起才可選",
  VOLATILITY_CONTEXT_UNAVAILABLE: "波動情境（ATR 百分位）尚未就緒",
  RECOVERY_FIRST_BAR: "暖機或資料失效後的恢復首根，重新累積確認中",
  WARMUP: "暖機未完成",
  WARMUP_BARS: "暖機未完成",
  DATA_INVALID: "這根 K 棒資料無效",
  COMPONENTS_UNAVAILABLE: "指標分量不可用",
  PARAMS_CHANGED_REQUIRES_REBUILD: "引擎參數中途變更，須重建狀態",
  BAR_REVISION_REQUIRES_REBUILD: "同一根 K 棒的內容被改寫，須重建狀態",
  REPLAY_IS_NOT_CURRENT: "重播的歷史結果，不代表現在",
  PENDING_RECOVERY: "資料失效後尚未恢復",
};

const regimeOrNull = (code) => REGIME_LABEL[code] || null;

// 帶參數的代碼（引擎用樣板字串組出來的）。子代碼對不到 → 整個代碼視為未翻譯。
const REASON_PATTERNS = [
  {
    re: /^CURRENT_EVIDENCE_DISAGREES_(.+)$/,
    zh: (m) => {
      const r = regimeOrNull(m[1]);
      return r ? `這一根的原始判定是「${r}」，與已確認狀態不一致` : null;
    },
  },
  {
    re: /^CONFIRMED_NOT_DIRECTIONAL_(.+)$/,
    zh: (m) => {
      const r = regimeOrNull(m[1]);
      return r ? `已確認狀態是「${r}」，不是多頭／空頭` : null;
    },
  },
  {
    re: /^CONFIRMED_NOT_RANGE_(.+)$/,
    zh: (m) => {
      const r = regimeOrNull(m[1]);
      return r ? `已確認狀態是「${r}」，不是盤整` : null;
    },
  },
  {
    re: /^TIME_GAP_RECOVERY_(\d+)_BARS_REMAINING$/,
    zh: (m) => `時間缺口後恢復中，還需 ${m[1]} 根正常間隔`,
  },
  {
    re: /^DATA_STATUS_(.+)$/,
    zh: (m) => (DATA_STATUS_LABEL[m[1]] ? `資料狀態：${DATA_STATUS_LABEL[m[1]]}` : null),
  },
];

/**
 * 代碼 → { code, text, translated }。
 * translated=false 時 text 是「原代碼〔未翻譯〕」；空值回「（空代碼）〔未翻譯〕」，一樣不會是空字串。
 */
export function reasonZh(code) {
  const c = code === null || code === undefined ? "" : String(code);
  if (!c) return { code: c, text: `（空代碼）${UNTRANSLATED_MARK}`, translated: false };
  if (Object.prototype.hasOwnProperty.call(REASON_EXACT, c)) {
    return { code: c, text: REASON_EXACT[c], translated: true };
  }
  for (const p of REASON_PATTERNS) {
    const m = p.re.exec(c);
    if (!m) continue;
    const t = p.zh(m);
    if (t) return { code: c, text: t, translated: true };
  }
  return { code: c, text: `${c}${UNTRANSLATED_MARK}`, translated: false };
}

/**
 * 一列判定的「狀態」該怎麼呈現。
 *
 * 引擎的 confirmedRegime 可以是 BULL／BEAR，但同一根不一定進方向池（結構矛盾、剛確認、時間缺口…）。
 * 只有 pool=directional 的 BULL／BEAR、或 pool=range 的 RANGE，才是引擎認為「這一根可以選」的狀態；
 * 其餘一律是**不採用**的原始判定，畫面上要明顯降階，不能長得像一個可用的多空方向。
 *
 *   adopted       這一根的狀態有沒有被路由採用
 *   isDirection   原始判定是不是多／空（含轉換中）——不採用時要特別標，避免被讀成方向
 *   inconsistent  pool 與 confirmedRegime 對不上（理論上不會發生；發生就是資料壞了，要看得到）
 */
export function regimeView(bar) {
  const code = bar ? bar.confirmedRegime : null;
  const pool = bar ? bar.pool : null;
  const known = Object.prototype.hasOwnProperty.call(REGIME_LABEL, code || "");
  const label = known ? REGIME_LABEL[code] : `${code || "（空代碼）"}${UNTRANSLATED_MARK}`;
  const isBullBear = code === "BULL" || code === "BEAR";
  const isDirection = isBullBear || code === "BULL_TRANSITION" || code === "BEAR_TRANSITION";
  const adopted = (pool === "directional" && isBullBear) || (pool === "range" && code === "RANGE");
  const inconsistent = (pool === "directional" && !isBullBear) || (pool === "range" && code !== "RANGE");
  return { code, label, known, adopted, isDirection, inconsistent };
}

/** 證據力分類（後端 evidenceClassOf 的四種值）。與 isBackfill 無關。 */
export const EVIDENCE_LABEL = {
  IN_TEST_WINDOW: { short: "檢定窗內", long: "凍結版引擎，K 棒落在正式檢定窗內" },
  POST_FREEZE: { short: "樣本外", long: "凍結版引擎，K 棒晚於凍結時點，但不在正式檢定窗內（不計入主分析）" },
  PRE_FREEZE: { short: "凍結前", long: "K 棒在凍結時點之前——設計引擎時已經看過，不是樣本外" },
  OTHER_ENGINE: { short: "非凍結版", long: "引擎或參數不是凍結版，前向檢定不稽核這種列" },
};

export function evidenceView(cls) {
  const e = EVIDENCE_LABEL[cls];
  if (e) return { code: cls, short: e.short, long: e.long, translated: true, outOfSample: cls === "IN_TEST_WINDOW" || cls === "POST_FREEZE" };
  const raw = `${cls || "（空代碼）"}${UNTRANSLATED_MARK}`;
  return { code: cls, short: raw, long: raw, translated: false, outOfSample: false };
}

/** 佔比顯示：null＝現行版本沒有樣本，顯示「—」而不是「0%」或「null%」 */
export function fmtPct(v) {
  return v === null || v === undefined || !Number.isFinite(Number(v)) ? "—" : `${v}%`;
}

/** sha 縮寫顯示 */
export function shortSha(s, n = 12) {
  const t = s === null || s === undefined ? "" : String(s);
  return t ? `${t.slice(0, n)}…` : "—";
}

/** 商品下拉的顯示文字（根數＝影子表裡這個商品的全部列）：停更的要帶「資料落後 X 天」 */
export function symbolOptionLabel(s) {
  if (!s) return "";
  const base = `${s.symbol}（${s.bars} 根）`;
  return s.stale ? `${base}｜資料落後 ${s.lagDays} 天` : base;
}
